import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, FlaskConical, X, Search } from "lucide-react";
import api, { formatPrice } from "@/lib/api";

const EMPTY = { name_kz: "", name_ru: "", description_kz: "", description_ru: "", category_kz: "", category_ru: "", price: 0, photo: "", is_available: true };

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterCat, setFilterCat] = useState("all");

  // Recipe state
  const [recipeOpen, setRecipeOpen] = useState(null);
  const [recipe, setRecipe] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [invSearch, setInvSearch] = useState("");
  const [savingRecipe, setSavingRecipe] = useState(false);

  const load = () => api.get("/admin/menu").then((r) => {
    setItems(r.data);
    setCategories([...new Set(r.data.map(i => i.category_ru || i.category || ""))]);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = filterCat === "all" ? items : items.filter(i => (i.category_ru || i.category) === filterCat);

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editId) await api.put(`/admin/menu/${editId}`, form);
      else await api.post("/admin/menu", form);
      setDialogOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch { alert("Ошибка"); }
    setLoading(false);
  };

  const handleEdit = (item) => { setForm(item); setEditId(item.id); setDialogOpen(true); };
  const handleDelete = async (id) => { if (window.confirm("Удалить?")) { await api.delete(`/admin/menu/${id}`); load(); } };

  // Recipe functions
  const openRecipe = async (item) => {
    setRecipeOpen(item);
    setInvSearch("");
    try {
      const [recipeRes, invRes] = await Promise.all([
        api.get(`/admin/menu/${item.id}/recipe`),
        api.get("/admin/inventory"),
      ]);
      setRecipe(recipeRes.data.recipe || []);
      setInventory(invRes.data || []);
    } catch {
      setRecipe([]);
      setInventory([]);
    }
  };

  const addIngredient = (inv) => {
    if (recipe.find(r => r.inventory_id === inv.id)) return;
    setRecipe([...recipe, { inventory_id: inv.id, name: inv.name, quantity: 1, unit: "порция" }]);
    setInvSearch("");
  };

  const updateIngredient = (idx, field, value) => {
    const updated = [...recipe];
    updated[idx] = { ...updated[idx], [field]: value };
    setRecipe(updated);
  };

  const removeIngredient = (idx) => {
    setRecipe(recipe.filter((_, i) => i !== idx));
  };

  const saveRecipe = async () => {
    if (!recipeOpen) return;
    setSavingRecipe(true);
    try {
      await api.put(`/admin/menu/${recipeOpen.id}/recipe`, { ingredients: recipe });
      load();
      setRecipeOpen(null);
    } catch { alert("Ошибка сохранения"); }
    setSavingRecipe(false);
  };

  const filteredInv = invSearch.trim()
    ? inventory.filter(i => i.name.toLowerCase().includes(invSearch.toLowerCase()) && !recipe.find(r => r.inventory_id === i.id))
    : [];

  return (
    <div data-testid="admin-menu">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Меню</h1>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground rounded-none gap-2" data-testid="add-menu-btn"><Plus className="w-4 h-4" /> Добавить</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif">{editId ? "Редактировать" : "Новая позиция"}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Название (KZ)</label>
                  <Input value={form.name_kz} onChange={(e) => setForm({ ...form, name_kz: e.target.value })} className="bg-background border-border rounded-none" data-testid="menu-name-kz" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Название (RU)</label>
                  <Input value={form.name_ru} onChange={(e) => setForm({ ...form, name_ru: e.target.value })} className="bg-background border-border rounded-none" data-testid="menu-name-ru" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Описание (KZ)</label>
                  <Input value={form.description_kz} onChange={(e) => setForm({ ...form, description_kz: e.target.value })} className="bg-background border-border rounded-none" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Описание (RU)</label>
                  <Input value={form.description_ru} onChange={(e) => setForm({ ...form, description_ru: e.target.value })} className="bg-background border-border rounded-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Категория (KZ)</label>
                  <Input value={form.category_kz} onChange={(e) => setForm({ ...form, category_kz: e.target.value })} className="bg-background border-border rounded-none" data-testid="menu-cat-kz" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Категория (RU)</label>
                  <Input value={form.category_ru} onChange={(e) => setForm({ ...form, category_ru: e.target.value })} className="bg-background border-border rounded-none" data-testid="menu-cat-ru" />
                </div>
              </div>
              <Input type="number" placeholder="Цена (тг)" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-border rounded-none" data-testid="menu-price-input" />
              <Button onClick={handleSave} disabled={loading} className="w-full bg-primary text-primary-foreground rounded-none" data-testid="menu-save-btn">
                {loading ? "..." : "Сохранить"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 overflow-x-auto pb-2">
        <button onClick={() => setFilterCat("all")} className={`px-3 py-1 text-xs whitespace-nowrap ${filterCat === "all" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-border"}`}>Все ({items.length})</button>
        {categories.map((c) => (
          <button key={c} onClick={() => setFilterCat(c)} className={`px-3 py-1 text-xs whitespace-nowrap ${filterCat === c ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-border"}`}>
            {c} ({items.filter(i => (i.category_ru || i.category) === c).length})
          </button>
        ))}
      </div>

      <div className="space-y-1">
        {filtered.map((item) => (
          <div key={item.id} className="flex items-center justify-between bg-card border border-border p-3" data-testid={`admin-menu-${item.id}`}>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-muted-foreground mr-2">{item.category_ru || item.category}</span>
              <span className="text-sm">{item.name_ru || item.name}</span>
              <span className="text-xs text-muted-foreground ml-1">/ {item.name_kz || ""}</span>
              {(item.description_ru || item.description) && <span className="text-xs text-muted-foreground ml-1">— {item.description_ru || item.description}</span>}
              {item.recipe && item.recipe.length > 0 && (
                <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-sm">
                  <FlaskConical className="w-2.5 h-2.5" /> {item.recipe.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-primary font-semibold whitespace-nowrap">{formatPrice(item.price)}</span>
              <button onClick={() => openRecipe(item)} className="text-muted-foreground hover:text-primary" title="Калькуляция" data-testid={`recipe-btn-${item.id}`}>
                <FlaskConical className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleEdit(item)} className="text-muted-foreground hover:text-foreground"><Pencil className="w-3 h-3" /></button>
              <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Recipe Dialog */}
      <Dialog open={!!recipeOpen} onOpenChange={(o) => { if (!o) setRecipeOpen(null); }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-primary" />
              Калькуляция — {recipeOpen?.name_ru}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-3 space-y-4">
            {/* Search inventory */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={invSearch}
                onChange={(e) => setInvSearch(e.target.value)}
                placeholder="Поиск ингредиента на складе..."
                className="pl-9 bg-background border-border rounded-none"
                data-testid="recipe-search-input"
              />
              {filteredInv.length > 0 && (
                <div className="absolute z-10 top-full left-0 right-0 bg-card border border-border max-h-40 overflow-y-auto shadow-lg">
                  {filteredInv.slice(0, 10).map((inv) => (
                    <button
                      key={inv.id}
                      onClick={() => addIngredient(inv)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-primary/10 flex justify-between"
                      data-testid={`add-ingredient-${inv.id}`}
                    >
                      <span>{inv.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {inv.quantity} {inv.unit} на складе
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Recipe ingredients list */}
            {recipe.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="recipe-empty">
                Калькуляция не задана. Найдите ингредиент выше и добавьте его.
              </p>
            ) : (
              <div className="space-y-2" data-testid="recipe-list">
                <div className="grid grid-cols-[1fr_100px_70px_32px] gap-2 text-[10px] text-muted-foreground uppercase tracking-wider px-1">
                  <span>Ингредиент</span>
                  <span>Порций</span>
                  <span></span>
                  <span></span>
                </div>
                {recipe.map((ing, idx) => (
                  <div key={ing.inventory_id} className="grid grid-cols-[1fr_100px_70px_32px] gap-2 items-center bg-background border border-border p-2 rounded-sm" data-testid={`recipe-item-${idx}`}>
                    <div className="text-sm truncate">{ing.name}</div>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      placeholder="1"
                      value={ing.quantity || ""}
                      onChange={(e) => updateIngredient(idx, "quantity", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                      onFocus={(e) => e.target.select()}
                      className="h-8 bg-card border-border rounded-none text-sm text-center px-1"
                      data-testid={`recipe-qty-${idx}`}
                    />
                    <span className="text-xs text-muted-foreground">порц.</span>
                    <button onClick={() => removeIngredient(idx)} className="text-red-400 hover:text-red-300 p-1" data-testid={`recipe-remove-${idx}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={saveRecipe}
              disabled={savingRecipe}
              className="w-full bg-primary text-primary-foreground rounded-none"
              data-testid="save-recipe-btn"
            >
              {savingRecipe ? "Сохранение..." : "Сохранить калькуляцию"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
